// The body of a failed-mutation toast, ported from vouchers: the friendly
// summary, a link to what blocked the action, field errors no form field
// showed, then a Details disclosure holding what support asks for.

import { CopyButton } from "@pej/kore/components";
import { ChevronDown, ExternalLink } from "lucide-react";
import { useState } from "react";
import { type ApiError, type FieldErrorSpec, UNKNOWN_CODE } from "@/api/error";
import { friendlyMessage } from "@/lib/friendly-error";

interface ErrorToastContentProps {
    err: ApiError;
    /** Field errors the form had no field for; listed so they still show. */
    unmapped?: ReadonlyArray<Pick<FieldErrorSpec, "path" | "message">>;
    /**
     * For links into the workspace. Passed by the caller: the toast renders
     * in a portal outside the router and the workspace context. Without it,
     * no link.
     */
    workspaceId?: string;
}

const UNMAPPED_LIMIT = 5;

interface BlockerLink {
    href: string;
    label: string;
}

/**
 * templates.rs's in-use refusal: the one screen by id, or the screens list
 * filtered by the template (kore's filter-url `Eq` is the bare key).
 */
function blockerLink(err: ApiError, workspaceId: string | undefined): BlockerLink | null {
    const p = err.params;
    if (err.code !== "Validation.ConstraintViolation" || !p?.templateId || !workspaceId) return null;
    const ws = encodeURIComponent(workspaceId);
    if (p.screenId && p.displayName) {
        return { href: `/w/${ws}/screens/${encodeURIComponent(p.screenId)}`, label: `Open "${p.displayName}"` };
    }
    if (p.screenCount) {
        return {
            href: `/w/${ws}/screens?templateId=${encodeURIComponent(p.templateId)}`,
            label: `Show the ${p.screenCount} screens`,
        };
    }
    return null;
}

export function ErrorToastContent({ err, unmapped, workspaceId }: ErrorToastContentProps) {
    const [open, setOpen] = useState(false);
    const code = err.code !== UNKNOWN_CODE ? err.code : undefined;
    const link = blockerLink(err, workspaceId);
    const visibleUnmapped = unmapped?.slice(0, UNMAPPED_LIMIT) ?? [];
    const hiddenUnmapped = (unmapped?.length ?? 0) - visibleUnmapped.length;

    return (
        <div className="space-y-1.5">
            <p className="text-sm">{friendlyMessage(err)}</p>
            {link ? (
                // Plain anchor in a new tab: the user is mid-flow, and the
                // toast sits outside the router.
                <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-medium text-kore-accent-text hover:underline"
                >
                    <ExternalLink className="size-3" />
                    {link.label}
                </a>
            ) : null}
            {visibleUnmapped.length > 0 ? (
                <ul className="space-y-0.5 text-xs text-kore-text-muted">
                    {visibleUnmapped.map((u) => (
                        <li key={u.path}>
                            <span className="font-mono">{u.path}</span>: {u.message}
                        </li>
                    ))}
                    {hiddenUnmapped > 0 ? <li className="italic">+{hiddenUnmapped} more</li> : null}
                </ul>
            ) : null}
            <div>
                <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpen((v) => !v)}
                    className="flex items-center gap-1 text-xs text-kore-text-muted transition-colors hover:text-kore-text"
                >
                    <ChevronDown className={`size-3 transition-transform ${open ? "rotate-180" : ""}`} />
                    Details
                </button>
                {open ? (
                    <dl className="mt-2 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-1.5 text-xs text-kore-text-muted">
                        <dt className="font-medium">Status</dt>
                        <dd className="font-mono">{err.status}</dd>
                        {code ? (
                            <>
                                <dt className="font-medium">Code</dt>
                                <dd className="flex min-w-0 items-center gap-1.5">
                                    <span className="truncate font-mono">{code}</span>
                                    <CopyButton value={code} label="Copy error code" size="sm" />
                                </dd>
                            </>
                        ) : null}
                        {err.requestId ? (
                            <>
                                <dt className="font-medium">Request ID</dt>
                                <dd className="flex min-w-0 items-center gap-1.5">
                                    <span className="truncate font-mono">{err.requestId}</span>
                                    <CopyButton value={err.requestId} label="Copy request ID" size="sm" />
                                </dd>
                            </>
                        ) : null}
                        {err.instance ? (
                            <>
                                <dt className="font-medium">Path</dt>
                                <dd className="truncate font-mono">{err.instance}</dd>
                            </>
                        ) : null}
                    </dl>
                ) : null}
            </div>
        </div>
    );
}
